'use client';

import React, { useState, useEffect, useCallback, ReactNode } from 'react';
import { getUserHasUnreadMsgs } from '@src/v3/services/chat';
import useProfile from '@src/v3/hooks/useProfile';
import UnreadMessagesContext from '@src/v3/contexts/unreadMessagesContext';

const UnreadMessagesProvider = ({ children }: { children: ReactNode }) => {
  const [hasUnreadCount, setHasUnreadCount] = useState<boolean>(false);
  const { isUserLoggedIn } = useProfile();

  // Get unread messages count
  const getUnreadMsgsCount = useCallback(async () => {
    try {
      const { has_unreads = false } = await getUserHasUnreadMsgs();
      setHasUnreadCount(has_unreads);
    } catch (error) {
      console.error(error);
      setHasUnreadCount(false);
    }
  }, []);

  // Poll for unread messages when user is logged in
  useEffect(() => {
    if (isUserLoggedIn) {
      // Fetch immediately when login state changes
      getUnreadMsgsCount();
      // Then poll every 10 seconds (subsequent calls)
      const interval = setInterval(() => {
        getUnreadMsgsCount();
      }, 10 * 1000);
      return () => clearInterval(interval);
    }
    // Reset to false when logged out
    setHasUnreadCount(false);
    return undefined;
  }, [getUnreadMsgsCount, isUserLoggedIn]);

  return (
    <UnreadMessagesContext.Provider
      value={{
        hasUnreadCount,
        refreshUnreadCount: getUnreadMsgsCount,
      }}
    >
      {children}
    </UnreadMessagesContext.Provider>
  );
};

export default UnreadMessagesProvider;
